import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Modal,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  AvatarGroup,
  Avatar,
  Typography,
  FormControl,
  Tooltip,
} from "@mui/material";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import ApiEndpoints from "../../network/ApiEndPoints";
import { postJsonData, get } from "../../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { whiteColor } from "../../theme/setThemeColor";
import AddIcon from "@mui/icons-material/Add";
import AuthContext from "../../store/AuthContext";
import CloseIcon from "@mui/icons-material/Close";
import Spinner from "../../component/loading-screen/Spinner";
import SmallLoader from "../../component/loading-screen/SmallLoader";
import { fileToBase64, splitIntoFlatChunks } from "../../utils/ImageUtil";

const CreateTaskForm = ({ project, refresh, getTasks, taskcreate }) => {
  const authCtx = useContext(AuthContext);
  const user = authCtx.user;
  const [request, setRequest] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("InProgress");
  const [priority, setPriority] = useState("Medium");
  const [category, setCategory] = useState("Frontend Development");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [developerId, setDeveloperId] = useState("");
  const [projectName, setProjectName] = useState(
    taskcreate && taskcreate === "true" ? project?.name : ""
  );
  const [projects, setProjects] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [formData, setFormData] = useState({
    project_id: project ? project.project_id : "",
    category: project ? project.category : "",
    start_date: project ? project.start_date : "",
    end_date: project ? project.end_date : "",
    assigned_to: project ? project.assigned_to : "",
    assigned_name: project ? project.assigned_name : "",
    priority: project ? project.priority : "",
    description: project ? project.description : "",
    status: project ? project.status : "",
    developer_remark: "",
    task_img: project ? project.task_img : "",
    weightage: "",
  });

  const [developers, setDevelopers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const fetchDevelopers = async () => {
    if (developers.length === 0) {
      setLoading(true);
      try {
        postJsonData(
          `${ApiEndpoints.GET_USERS_NAME}`,
          { role: "developer" },
          setLoading,
          (res) => {
            setDevelopers(res.data || []);
          },
          (error) => {
            apiErrorToast(error);
          }
        );
      } catch (error) {
        apiErrorToast("Failed to fetch developers");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleButtonClick = (number) => {
    setFormData({ ...formData, weightage: number });
  };

  const fetchProjects = async () => {
    if (projects.length === 0) {
      setLoading(true);
      try {
        get(
          ApiEndpoints.GET_PROJECTS,
          "",
          setLoading,
          (res) => {
            setProjects(res.data.data || []);
          },
          (error) => {
            apiErrorToast(error);
          }
        );
      } catch (error) {
        apiErrorToast("Failed to fetch projects");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFieldFocus = (field) => {
    const typeMap = {
      priority: "priority",
      category: "category",
      status: "TaskStatus",
    };

    const type = typeMap[field];
    if (!type) return;

    const url = `${ApiEndpoints.GET_UTILS}?type=${type}`;

    get(
      url,
      "",
      null,
      (res) => {
        const options = res.data.data || [];
        const names = options.map((item) => item.name);

        if (type === "priority") setPriorityOptions(names);
        else if (type === "category") setCategoryOptions(names);
        else if (type === "TaskStatus") setStatusOptions(names);

        setRequest(false);
      },
      (error) => {
        apiErrorToast(error);
        setRequest(false);
      }
    );
  };

  const handleProjectChange = (event) => {
    const selectedProjectName = event.target.value;
    const selectedProject = projects.find(
      (project) => project.name === selectedProjectName
    );

    setProjectName(selectedProjectName);
    setFormData({ ...formData, project_id: selectedProject?.id || "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setRequest(true);

    try {
      // --------------------
      // Validations
      // --------------------
      if (!projectName) {
        apiErrorToast("Please select a project");
        return;
      }

      if (!formData.description) {
        apiErrorToast("Please fill the description field");
        return;
      }

      if (
        !(user.role === "Merchant" || user.role === "Backend") &&
        !formData.end_date
      ) {
        apiErrorToast("Please select an end date.");
        return;
      }

      if (!formData.start_date) {
        apiErrorToast("Please select a start date.");
        return;
      }

      // --------------------
      // Final End Date
      // --------------------
      const finalEndDate =
        user.role === "Merchant" || user.role === "Backend"
          ? formData.start_date
          : formData.end_date;

      // --------------------
      // Assigned Developer
      // --------------------
      const selectedDeveloper = developers.find(
        (dev) => dev.id === developerId
      );

      const assignedName = selectedDeveloper?.name || "";

      // --------------------
      // 1️⃣ Build JSON Payload
      // --------------------
      const payload = {};

      payload.project_id =
        taskcreate === "true" ? project?.id : formData.project_id;

      payload.project_name = projectName;
      payload.start_date = formData.start_date;
      payload.end_date = finalEndDate;
      payload.priority = priority;
      payload.category = category;
      payload.description = formData.description;
      payload.developer_remark = formData.developer_remark;

      if (user.role !== "Merchant") {
        payload.status = status;
      }

      if (user.role !== "Merchant" && developerId) {
        payload.assigned_to = developerId;
        payload.assigned_name = assignedName;
      }

      if (user.role === "Admin" && formData.weightage) {
        payload.weightage = formData.weightage;
      }

      // --------------------
      // 2️⃣ Image → Base64 → Chunks
      // --------------------
      if (image) {
        const base64 = await fileToBase64(image);
        const imageChunks = splitIntoFlatChunks(base64, "task_img");
        Object.assign(payload, imageChunks);
      }

      console.log("CREATE TASK PAYLOAD", payload);

      // --------------------
      // 3️⃣ API Call
      // --------------------
      postJsonData(
        ApiEndpoints.CREATE_TASK,
        payload,
        setRequest,
        (res) => {
          okSuccessToast(res?.data?.message || "Task created successfully");

          // Reset
          setFormData({
            project_id: "",
            start_date: "",
            end_date: "",
            assigned_to: "",
            priority: "",
            category: "",
            description: "",
            developer_remark: "",
            status: "",
            weightage: "",
          });

          setImage(null);
          setDeveloperId("");
          setProjectName("");
          setOpen(false);

          if (refresh) refresh();
          if (getTasks) getTasks();
        },
        apiErrorToast
      );
    } catch (err) {
      console.error(err);
      apiErrorToast("Task creation failed");
    } finally {
      setRequest(false);
    }
  };

  const handleCloseModal = () => {
    setDeveloperId("");
    setProjectName("");
    setOpen(false);
    setCategory("");
    setPriority("");
    setStatus("");
    setImage(null);
    setFormData({ ...formData, weightage: "" });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setFormData((prev) => ({
        ...prev,
        task_img: file.name,
      }));
    }
  };

  const handelOpen = () => {
    setOpen(true);
    handleFieldFocus("category");
    handleFieldFocus("status");
    handleFieldFocus("priority");
    fetchDevelopers();
  };

  return (
    <>
      {!taskcreate ? (
        <Button
          startIcon={
            <IconButton sx={{ p: 0, color: whiteColor() }}>
              <AddCircleOutlineIcon />
            </IconButton>
          }
          size="small"
          color="success"
          variant="contained"
          sx={{
            padding: 1,
            backgroundColor: "#2F3B57",
            borderRadius: "2px",
            color: whiteColor(),
          }}
          onClick={handelOpen}
        >
          Create Task
        </Button>
      ) : (
        <Box display="flex" alignItems="center" onClick={() => setOpen(true)}>
          <Tooltip title="Add Task">
            <Avatar sx={{ bgcolor: "#3F3D56", width: 40, height: 40, ml: 2 }}>
              <AddIcon sx={{ color: "#F4A261" }} />
            </Avatar>
          </Tooltip>
        </Box>
      )}
      <Modal
        open={open}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            width: {
              xs: "95%",
              sm: "85%",
              md: "75%",
              lg: "65%",
              xl: "55%",
            },
            maxWidth: "900px",
            maxHeight: "90vh",
            overflowY: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SmallLoader loading={request} />
          <Box sx={{ flexShrink: 0 }}>
            <ModalHeader title="Create Task" handleClose={handleCloseModal} />
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            id="add-task"
            noValidate
            autoComplete="off"
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 2, sm: 3, md: 4 },
              pb: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Grid container spacing={1.5}>
              {/* Project Name & ID - Same line on desktop */}
              <Grid item xs={12} sm={6} md={6}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  display="block"
                  gutterBottom
                >
                  Project Name
                </Typography>
                {taskcreate && taskcreate === "true" ? (
                  <TextField
                    fullWidth
                    value={project?.name}
                    className="textfield"
                    required
                    disabled={taskcreate && taskcreate === "true"}
                    size="small"
                    sx={{ "& .MuiInputBase-root": { height: 36 } }}
                  />
                ) : (
                  <TextField
                    select
                    fullWidth
                    value={projectName}
                    onFocus={fetchProjects}
                    onChange={handleProjectChange}
                    required
                    className="textfield"
                    size="small"
                    sx={{ "& .MuiInputBase-root": { height: 36 } }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: { maxHeight: 200 },
                        },
                      },
                    }}
                  >
                    {loading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      projects.map((project) => (
                        <MenuItem key={project.id} value={project.name}>
                          {project.name}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  display="block"
                  gutterBottom
                >
                  Project ID
                </Typography>
                {taskcreate && taskcreate === "true" ? (
                  <TextField
                    fullWidth
                    type="number"
                    value={project?.id}
                    required
                    className="textfield"
                    disabled={true}
                    size="small"
                    sx={{ "& .MuiInputBase-root": { height: 36 } }}
                  />
                ) : (
                  <TextField
                    name="project_id"
                    fullWidth
                    type="number"
                    value={formData.project_id}
                    InputProps={{ readOnly: true }}
                    required
                    className="textfield"
                    onChange={(e) =>
                      setFormData({ ...formData, project_id: e.target.value })
                    }
                    size="small"
                    sx={{ "& .MuiInputBase-root": { height: 36 } }}
                  />
                )}
              </Grid>

              {/* Start Date & End Date */}
              <Grid item xs={12} sm={6} md={6}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  display="block"
                  gutterBottom
                >
                  Start Date
                </Typography>
                <TextField
                  name="start_date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                  className="textfield"
                  size="small"
                  sx={{ "& .MuiInputBase-root": { height: 36 } }}
                />
              </Grid>

              {!(user.role === "Merchant" || user.role === "Backend") && (
                <Grid item xs={12} sm={6} md={6}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    display="block"
                    gutterBottom
                  >
                    End Date
                  </Typography>
                  <TextField
                    name="end_date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={formData.end_date}
                    onChange={handleChange}
                    required
                    className="textfield"
                    size="small"
                    sx={{ "& .MuiInputBase-root": { height: 36 } }}
                  />
                </Grid>
              )}

              {/* Category & Priority */}
              {user.role !== "Backend" && (
                <>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      gutterBottom
                    >
                      Category
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      onFocus={() => handleFieldFocus("category")}
                      required
                      className="textfield"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { height: 36 } }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { maxHeight: 200 },
                          },
                        },
                      }}
                    >
                      {categoryOptions.map((categoryOption, index) => (
                        <MenuItem key={index} value={categoryOption}>
                          {categoryOption}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      gutterBottom
                    >
                      Priority
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      onFocus={() => handleFieldFocus("priority")}
                      required
                      className="textfield"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { height: 36 } }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { maxHeight: 200 },
                          },
                        },
                      }}
                    >
                      {priorityOptions.map((priorityOption, index) => (
                        <MenuItem key={index} value={priorityOption}>
                          {priorityOption}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}

              {/* Status & Assigned To */}
              {!(user.role === "Merchant" || user.role === "Backend") && (
                <>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      gutterBottom
                    >
                      Status
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      onFocus={() => handleFieldFocus("status")}
                      required
                      className="textfield"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { height: 36 } }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { maxHeight: 200 },
                          },
                        },
                      }}
                    >
                      {statusOptions.map((statusOption, index) => (
                        <MenuItem key={index} value={statusOption}>
                          {statusOption}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      gutterBottom
                    >
                      Assigned To
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={developerId}
                      onFocus={fetchDevelopers}
                      onChange={(e) => setDeveloperId(e.target.value)}
                      required
                      className="textfield"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { height: 36 } }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { maxHeight: 200 },
                          },
                        },
                      }}
                    >
                      {loading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        developers.map((developer) => (
                          <MenuItem key={developer.id} value={developer.id}>
                            {developer.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                </>
              )}

              {/* Description */}
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  display="block"
                  gutterBottom
                >
                  Description
                </Typography>
                <TextField
                  name="description"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="textfield"
                  size="small"
                  sx={{ "& .MuiInputBase-root": { minHeight: 36 } }}
                />
              </Grid>

              {/* Weightage for Admin */}
              {user.role === "Admin" && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      gutterBottom
                    >
                      Weightage
                    </Typography>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap={0.5}
                      justifyContent="center"
                      sx={{ width: "100%" }}
                    >
                      {[...Array(10).keys()].map((number) => {
                        const buttonNumber = number + 1;
                        let buttonColor;
                        if (buttonNumber === 1 || buttonNumber === 2) {
                          buttonColor = "red";
                        } else if (buttonNumber === 3) {
                          buttonColor = "orange";
                        } else if ([4, 5, 6].includes(buttonNumber)) {
                          buttonColor = "#FFC000";
                        } else if ([7, 8].includes(buttonNumber)) {
                          buttonColor = "#AAFF00";
                        } else {
                          buttonColor = "#006400";
                        }

                        const isSelected = formData.weightage === buttonNumber;

                        return (
                          <Button
                            key={buttonNumber}
                            variant={isSelected ? "contained" : "outlined"}
                            onClick={() => handleButtonClick(buttonNumber)}
                            sx={{
                              backgroundColor: isSelected
                                ? "#000"
                                : buttonColor,
                              borderColor: isSelected ? "#000" : buttonColor,
                              color: "white",
                              "&:hover": {
                                backgroundColor: isSelected
                                  ? "#333"
                                  : buttonColor,
                              },
                              minWidth: "30px",
                              height: "30px",
                              fontSize: "0.75rem",
                              padding: "0",
                            }}
                          >
                            {buttonNumber}
                          </Button>
                        );
                      })}
                    </Box>

                    {formData.weightage && (
                      <Box
                        sx={{
                          mt: 1,
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "#555",
                          textAlign: "center",
                        }}
                      >
                        Selected Weightage: {formData.weightage * 10}%
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Image Upload */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <input
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                    id="task_img"
                    style={{ marginTop: 4, fontSize: "14px" }}
                  />
                  {image && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      <strong>Selected Image: </strong>
                      {image.name}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                pt: 1,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <ModalFooter
                form="add-task"
                request={request}
                btn="Create Task"
                disable={formData.start_date === ""}
              />
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CreateTaskForm;
